import { createI18n } from 'vue-i18n'

// Keep locale IDs consistent everywhere (filenames + i18n locale + html lang)
export const SUPPORTED_LOCALES = ['en', 'es', 'zh-hans']

const DEFAULT_LOCALE = 'en'
const FALLBACK_LOCALE = 'en'

function toHtmlLang(locale) {
    switch (locale) {
        case "zh-hans":
            return "zh-Hans"; // BCP-47
        case "es":
            return "es";
        case "en":
        default:
            return "en";
    }
}

function normalizeLocale(input) {
    const raw = (input || '').trim()
    if (!raw) return DEFAULT_LOCALE

    const lowered = raw.toLowerCase()

    // Reduce browser locales like "es-ES", "zh-CN", etc.
    if (lowered.startsWith('es')) return 'es'
    if (lowered.startsWith('zh')) return 'zh-hans'
    if (lowered.startsWith('en')) return 'en'

    // Only allow known locales (exact match)
    return SUPPORTED_LOCALES.includes(lowered) ? lowered : DEFAULT_LOCALE
}

// --- Eager-load English (sync, webpack-safe) ---
// Also eager-load critical goals so the SDG UI has strings immediately.
const en = require('./locales/en.json')
const enCriticalGoals = require('./locales/en_criticalGoals.json')

const messages = {
    en: {
        ...(en.default || en),
        criticalGoals: (enCriticalGoals.default || enCriticalGoals),
    },
}

export const i18n = createI18n({
    legacy: false,
    locale: DEFAULT_LOCALE,
    fallbackLocale: FALLBACK_LOCALE,
    messages,
    // Vue CLI / Webpack uses process.env.NODE_ENV (not import.meta.env)
    missingWarn: process.env.NODE_ENV !== 'production',
    fallbackWarn: process.env.NODE_ENV !== 'production',
})

const loaded = new Set(['en'])

function loadLocaleMessages(locale) {
    if (loaded.has(locale)) return Promise.resolve()

    // Load BOTH the base locale strings and the critical goals JSON,
    // then merge them under the same message tree shape.
    return Promise.all([
        import(
            /* webpackChunkName: "lang-[request]" */
            `./locales/${locale}.json`
            ),
        import(
            /* webpackChunkName: "lang-[request]-criticalGoals" */
            `./locales/${locale}_criticalGoals.json`
            ),
    ]).then(([baseModule, criticalGoalsModule]) => {
        const baseMsgs = baseModule.default || baseModule
        const criticalGoalsMsgs = criticalGoalsModule.default || criticalGoalsModule

        i18n.global.setLocaleMessage(locale, {
            ...baseMsgs,
            criticalGoals: criticalGoalsMsgs,
        })

        loaded.add(locale)
    })
}



export function setLocale(locale) {
    const next = normalizeLocale(locale)

    return loadLocaleMessages(next).then(() => {
        i18n.global.locale.value = next

        // Set proper html lang so the native file input text localizes
        document.documentElement.setAttribute("lang", toHtmlLang(next))

        localStorage.setItem("locale", next)
        return next
    })
}


export function initLocale() {
    const saved = localStorage.getItem('locale')

    // Optional: default from browser language if none saved
    const browser = navigator.language || (navigator.languages && navigator.languages[0])
    const initial = saved || browser || DEFAULT_LOCALE

    return setLocale(initial)
}

export default i18n